import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, TrendingUp, Gift, Star, Clock, DollarSign } from 'lucide-react';
import PageSeo from '../components/PageSeo';
import SchemaMarkup from '../components/SchemaMarkup';
import { pageSchemas } from '../data/pageSchemas';

const SeasonalTrends = () => {
  const [selectedSeason, setSelectedSeason] = useState('current');

  useEffect(() => {
    // Analytics only (SEO metadata handled by PageSeo)
    if (typeof gtag !== 'undefined') {
      gtag('event', 'page_view', {
        'page_title': 'Gift Card Seasonal Trends 2024-2025',
        'page_location': window.location.href,
        'event_category': 'seo_content'
      });
    }
  }, []);

  const seasons = {
    current: {
      name: 'October 2024 - Current Trends',
      period: 'October 2024',
      description: 'Back-to-school season winds down, early holiday preparation begins',
      topBrands: [
        { name: 'Amazon', rate: '88-93%', trend: 'up', reason: 'Early holiday shopping preparation' },
        { name: 'Apple', rate: '82-89%', trend: 'up', reason: 'iPhone 16 launch season' },
        { name: 'Best Buy', rate: '80-86%', trend: 'up', reason: 'Electronics demand increase' },
        { name: 'Target', rate: '82-87%', trend: 'steady', reason: 'Consistent retail demand' },
        { name: 'Walmart', rate: '83-88%', trend: 'up', reason: 'Holiday inventory building' }
      ],
      insights: [
        'Early holiday shopping drives gift card demand up 15%',
        'Electronics brands see highest rate increases',
        'Fashion brands prepare for holiday fashion surge',
        'Gaming cards stable as new releases approach'
      ]
    },
    holiday: {
      name: 'Holiday Season (Nov-Jan)',
      period: 'November 2024 - January 2025',
      description: 'Peak gift card season with highest demand and rates',
      topBrands: [
        { name: 'Amazon', rate: '90-95%', trend: 'up', reason: 'Peak holiday shopping demand' },
        { name: 'Apple', rate: '85-92%', trend: 'up', reason: 'Holiday gift purchases' },
        { name: 'Target', rate: '85-90%', trend: 'up', reason: 'Holiday shopping surge' },
        { name: 'Best Buy', rate: '82-89%', trend: 'up', reason: 'Electronics gift season' },
        { name: 'Starbucks', rate: '78-85%', trend: 'up', reason: 'Holiday beverage season' }
      ],
      insights: [
        'Gift card rates reach annual peaks (up to 25% higher)',
        'Amazon dominates with near face-value rates',
        'Fashion and electronics see massive demand spikes',
        'Post-holiday selling still maintains premium rates'
      ]
    },
    spring: {
      name: 'Spring Season (Feb-May)',
      period: 'February - May 2025',
      description: 'Post-holiday normalization with gradual recovery',
      topBrands: [
        { name: 'Home Depot', rate: '82-88%', trend: 'up', reason: 'Spring home improvement season' },
        { name: "Lowe's", rate: '80-86%', trend: 'up', reason: 'Gardening and projects' },
        { name: 'Amazon', rate: '85-90%', trend: 'steady', reason: 'Consistent year-round demand' },
        { name: 'Nike', rate: '78-85%', trend: 'up', reason: 'Spring fitness motivation' },
        { name: 'Spotify', rate: '70-78%', trend: 'steady', reason: 'Digital entertainment' }
      ],
      insights: [
        'Home improvement brands dominate spring trends',
        'Fitness and outdoor brands see rate increases',
        'General retail rates normalize after holiday peak',
        'Gift card volume decreases but rates stabilize'
      ]
    },
    summer: {
      name: 'Summer Season (Jun-Aug)',
      period: 'June - August 2025',
      description: 'Travel and entertainment focus with back-to-school preparation',
      topBrands: [
        { name: 'Amazon', rate: '86-91%', trend: 'steady', reason: 'Summer shopping needs' },
        { name: 'Best Buy', rate: '80-87%', trend: 'up', reason: 'Back-to-school electronics' },
        { name: 'Target', rate: '81-86%', trend: 'up', reason: 'Back-to-school supplies' },
        { name: 'Nike', rate: '80-87%', trend: 'up', reason: 'Summer sports activities' },
        { name: 'Steam', rate: '75-82%', trend: 'up', reason: 'Summer gaming sales' }
      ],
      insights: [
        'Back-to-school preparation drives rates up',
        'Travel and entertainment cards see summer boost',
        'Gaming platforms peak during summer sales',
        'Fashion brands prepare for fall collections'
      ]
    }
  };

  const marketEvents = [
    {
      date: 'Black Friday 2024',
      impact: 'Very High',
      description: 'Gift card rates spike 20-30% across all major brands',
      brands: ['Amazon', 'Target', 'Walmart', 'Best Buy'],
      strategy: 'Hold cards until this period for maximum value'
    },
    {
      date: 'Cyber Monday 2024', 
      impact: 'High',
      description: 'Digital and tech brand cards see significant rate increases',
      brands: ['Apple', 'Google Play', 'Steam', 'PlayStation'],
      strategy: 'Perfect time to sell tech-related gift cards'
    },
    {
      date: 'iPhone 16 Launch',
      impact: 'Medium',
      description: 'Apple gift cards experience rate boost during launch period',
      brands: ['Apple', 'iTunes'],
      strategy: 'Sell Apple cards during product launch windows'
    },
    {
      date: 'Steam Summer Sale 2025',
      impact: 'Medium',
      description: 'Gaming gift cards see increased demand',
      brands: ['Steam', 'Xbox', 'PlayStation', 'Nintendo'],
      strategy: 'Hold gaming cards until major sale events'
    },
    {
      date: 'Prime Day 2025',
      impact: 'High',
      description: 'Amazon gift cards reach peak rates during Prime events',
      brands: ['Amazon'],
      strategy: 'Amazon cards always perform well during Prime events'
    }
  ];

  const getCurrentSeason = () => {
    const month = new Date().getMonth() + 1; // 1-12
    if (month >= 11 || month <= 1) return 'holiday';
    if (month >= 2 && month <= 5) return 'spring';
    if (month >= 6 && month <= 8) return 'summer';
    return 'current'; // Fall season
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'Very High': return 'bg-red-100 text-red-800 border-red-300';
      case 'High': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getTrendIcon = (trend) => {
    return trend === 'up' ? 
      <TrendingUp className="text-green-500" size={16} /> : 
      <TrendingUp className="text-gray-500 transform rotate-180" size={16} />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <PageSeo
        title="Gift Card Seasonal Trends & Market Analysis | CashifyGCMart"
        description="Complete analysis of gift card market trends. Seasonal demand patterns, best selling times, and rate forecasts for all major brands."
        path="/seasonal-trends"
      />
      <SchemaMarkup article={pageSchemas.seasonalTrends.article} />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="flex justify-center mb-6">
            <Calendar size={64} className="text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Gift Card Market Trends 2024-2025
          </h1>
          <p className="text-xl md:text-2xl opacity-90 mb-8">
            Seasonal analysis, demand patterns, and optimal selling strategies
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <span className="bg-white/20 px-4 py-2 rounded-full">📊 Market Analysis</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">📅 Seasonal Trends</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">💡 Selling Tips</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Current Market Status */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-12">
          <h2 className="text-2xl font-bold mb-4 text-blue-800 flex items-center">
            <Clock className="mr-2" size={24} />
            Current Market Status - October 2024
          </h2>
          <div className="grid md:grid-cols-3 gap-4 text-center">
            <div className="bg-white p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-600">📈 Rising</div>
              <div className="text-sm text-gray-600">Market Trend</div>
            </div>
            <div className="bg-white p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">87%</div>
              <div className="text-sm text-gray-600">Average Rate</div>
            </div>
            <div className="bg-white p-4 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">High</div>
              <div className="text-sm text-gray-600">Demand Level</div>
            </div>
          </div>
        </div>

        {/* Season Selector */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-6 text-gray-800">Seasonal Analysis</h2>
          <div className="flex flex-wrap gap-2 mb-6">
            {Object.entries(seasons).map(([key, season]) => (
              <button
                key={key}
                onClick={() => setSelectedSeason(key)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  selectedSeason === key 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {season.name}
              </button>
            ))}
          </div>

          {/* Selected Season Details */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                {seasons[selectedSeason].name}
              </h3>
              <p className="text-gray-600 mb-4">{seasons[selectedSeason].description}</p>
              <div className="text-sm text-blue-600 font-semibold">
                Period: {seasons[selectedSeason].period}
              </div>
            </div>

            {/* Top Performing Brands */}
            <div className="mb-8">
              <h4 className="text-xl font-bold mb-4 text-gray-800">Top Performing Brands</h4>
              <div className="space-y-4">
                {seasons[selectedSeason].topBrands.map((brand, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className="bg-white p-2 rounded-full">
                        <Star className="text-yellow-500" size={20} />
                      </div>
                      <div>
                        <h5 className="font-bold text-lg">{brand.name}</h5>
                        <p className="text-sm text-gray-600">{brand.reason}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-xl font-bold text-green-600">{brand.rate}</span>
                        {getTrendIcon(brand.trend)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Season Insights */}
            <div>
              <h4 className="text-xl font-bold mb-4 text-gray-800">Key Insights</h4>
              <div className="grid md:grid-cols-2 gap-4">
                {seasons[selectedSeason].insights.map((insight, index) => (
                  <div key={index} className="flex items-start space-x-3 bg-blue-50 p-4 rounded-lg">
                    <div className="bg-blue-500 text-white rounded-full p-1 mt-1">
                      <TrendingUp size={12} />
                    </div>
                    <p className="text-gray-700">{insight}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Market Events Calendar */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-800 flex items-center">
            <Gift className="mr-3 text-purple-500" size={32} />
            Major Market Events & Opportunities
          </h2>
          
          <div className="grid gap-6">
            {marketEvents.map((event, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <h3 className="text-xl font-bold text-gray-800 mr-4">{event.date}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getImpactColor(event.impact)}`}>
                        {event.impact} Impact
                      </span>
                    </div>
                    <p className="text-gray-600 mb-3">{event.description}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-3">
                      {event.brands.map((brand, brandIndex) => (
                        <span key={brandIndex} className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm">
                          {brand}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r">
                  <p className="text-sm text-green-700">
                    <strong>💡 Strategy:</strong> {event.strategy}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Monthly Rate Forecast */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-800">12-Month Rate Forecast</h2>
          
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 p-3 text-left">Month</th>
                    <th className="border border-gray-300 p-3 text-left">Amazon</th>
                    <th className="border border-gray-300 p-3 text-left">Apple</th>
                    <th className="border border-gray-300 p-3 text-left">Target</th>
                    <th className="border border-gray-300 p-3 text-left">Market Trend</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 p-3 font-semibold">Oct 2024</td>
                    <td className="border border-gray-300 p-3 text-green-600">88-93%</td>
                    <td className="border border-gray-300 p-3 text-green-600">82-89%</td>
                    <td className="border border-gray-300 p-3 text-green-600">82-87%</td>
                    <td className="border border-gray-300 p-3">📈 Rising</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 p-3 font-semibold">Nov 2024</td>
                    <td className="border border-gray-300 p-3 text-green-600">90-95%</td>
                    <td className="border border-gray-300 p-3 text-green-600">85-92%</td>
                    <td className="border border-gray-300 p-3 text-green-600">85-90%</td>
                    <td className="border border-gray-300 p-3">🚀 Peak</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-3 font-semibold">Dec 2024</td>
                    <td className="border border-gray-300 p-3 text-green-600">92-95%</td>
                    <td className="border border-gray-300 p-3 text-green-600">87-94%</td>
                    <td className="border border-gray-300 p-3 text-green-600">87-92%</td>
                    <td className="border border-gray-300 p-3">🔥 Highest</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 p-3 font-semibold">Jan 2025</td>
                    <td className="border border-gray-300 p-3 text-blue-600">88-93%</td>
                    <td className="border border-gray-300 p-3 text-blue-600">83-90%</td>
                    <td className="border border-gray-300 p-3 text-blue-600">83-88%</td>
                    <td className="border border-gray-300 p-3">📉 Normalizing</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="mt-6 bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Note:</strong> Rates are estimates based on historical data and market analysis. 
                Actual rates may vary based on market conditions and individual card factors.
              </p>
            </div>
          </div>
        </section>

        {/* Selling Strategy Guide */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-800">Optimal Selling Strategies</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 className="text-xl font-bold text-green-800 mb-4 flex items-center">
                <DollarSign className="mr-2" size={24} />
                High-Value Timing
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start space-x-2">
                  <span className="bg-green-500 text-white rounded-full p-1 mt-1">
                    <Calendar size={10} />
                  </span>
                  <span><strong>November-December:</strong> Hold for holiday peak rates</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="bg-green-500 text-white rounded-full p-1 mt-1">
                    <Calendar size={10} />
                  </span>
                  <span><strong>Product Launches:</strong> Sell tech cards during new releases</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="bg-green-500 text-white rounded-full p-1 mt-1">
                    <Calendar size={10} />
                  </span>
                  <span><strong>Sale Events:</strong> Gaming cards during Steam/console sales</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="bg-green-500 text-white rounded-full p-1 mt-1">
                    <Calendar size={10} />
                  </span>
                  <span><strong>Seasonal Peaks:</strong> Home improvement in spring</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-xl font-bold text-blue-800 mb-4 flex items-center">
                <Clock className="mr-2" size={24} />
                Quick Sale Timing
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start space-x-2">
                  <span className="bg-blue-500 text-white rounded-full p-1 mt-1">
                    <Clock size={10} />
                  </span>
                  <span><strong>Immediate Need:</strong> Amazon cards maintain good rates year-round</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="bg-blue-500 text-white rounded-full p-1 mt-1">
                    <Clock size={10} />
                  </span>
                  <span><strong>Expiration Risk:</strong> Sell before rates drop due to time pressure</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="bg-blue-500 text-white rounded-full p-1 mt-1">
                    <Clock size={10} />
                  </span>
                  <span><strong>Market Uncertainty:</strong> Secure current rates if unsure about trends</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="bg-blue-500 text-white rounded-full p-1 mt-1">
                    <Clock size={10} />
                  </span>
                  <span><strong>Portfolio Balance:</strong> Diversify timing across different cards</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-8 rounded-lg text-center">
          <h2 className="text-3xl font-bold mb-4">Make Informed Selling Decisions</h2>
          <p className="text-xl mb-6">Use our tools to maximize your gift card value based on current market trends</p>
          <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
            <Link to="/rate-calculator" className="block sm:inline-block bg-white text-indigo-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Check Current Rates
            </Link>
            <Link to="/gift-card-values" className="block sm:inline-block bg-indigo-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-indigo-800 transition-colors border border-white">
              View All Brand Values
            </Link>
          </div>
        </div>
      </div>

      {/* SEO content section — deeper explanation for search engines and shoppers */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 prose max-w-none">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Why gift card resale rates move with the seasons</h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          Gift card resale prices aren't static — they climb and fall throughout the year based
          on shopper demand, retailer promotions, and card-issuing volume. Knowing when to sell
          can meaningfully change how much cash you walk away with. Rates for a $100 Amazon
          card, for example, can vary by 3–5 percentage points between a slow February week and
          a Black Friday buying peak.
        </p>

        <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">The four windows that matter most</h3>
        <p className="text-gray-700 leading-relaxed mb-2">
          <strong>Holiday season (late Nov – Dec 31).</strong> Demand for gift cards spikes as
          shoppers hunt for last-minute presents. Resale rates for Amazon, Apple, Walmart, Target
          and Visa cards typically hit their annual highs. If you can sell before your buyer
          demand peaks, you'll pocket a few extra dollars per card.
        </p>
        <p className="text-gray-700 leading-relaxed mb-2">
          <strong>Post-holiday liquidation (Jan – early Feb).</strong> This is when the market
          floods with gift cards people received but don't want. Supply outpaces demand and rates
          soften for a few weeks. Sell early January if you can — waiting three weeks can shave
          2–4% off your payout.
        </p>
        <p className="text-gray-700 leading-relaxed mb-2">
          <strong>Back-to-school (Aug – early Sept).</strong> Rates for Apple, Best Buy, and Target
          jump as parents and students stock up on electronics and supplies. Tech-heavy retailers
          see the biggest bump.
        </p>
        <p className="text-gray-700 leading-relaxed mb-4">
          <strong>Mid-summer lull (June – July).</strong> A predictable quiet stretch. If you're
          holding cards you don't need, mid-summer is usually the worst time to sell — rates dip
          a couple of points across the board.
        </p>

        <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Brand-specific patterns</h3>
        <ul className="space-y-2 text-gray-700">
          <li><strong>Steam gift cards</strong> peak during the Summer Sale (late June) and Winter Sale (late December).</li>
          <li><strong>Nike, Lululemon, Dick's Sporting Goods</strong> spike around New Year fitness resolutions and back-to-school.</li>
          <li><strong>Sephora, Ulta</strong> climb through Q4 gift-giving season and Valentine's/Mother's Day windows.</li>
          <li><strong>Disney, Southwest</strong> peak in spring break and summer vacation planning months.</li>
          <li><strong>Home Depot, Lowe's</strong> rise in spring (April–May) as homeowners kick off renovation season.</li>
        </ul>

        <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">How to time your sale like a pro</h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          Bookmark the <Link to="/rate-calculator" className="text-pink-600 underline">rate calculator</Link>
          and check your card's payout weekly. When you see it climb 2%+ above the recent average,
          that's a strong signal demand has spiked — lock in your sale while rates are elevated.
          Cards are stored value; they don't earn interest sitting in a drawer, so holding for
          a small rate bump rarely beats selling now and putting the cash to work.
        </p>

        <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Ready to sell?</h3>
        <p className="text-gray-700 leading-relaxed">
          Use the <Link to="/form-submission" className="text-pink-600 underline">submission form</Link>
          to lock in today's rate. Payouts land the same business day for verified digital cards
          from major brands.
        </p>
      </section>
    </div>
  );
};

export default SeasonalTrends;